import { getExecutingEnvironment } from "@app/resolveModule/env";
import Url from "@core-ui/ApiServices/Types/Url";
import assert from "assert";

const GRAMAX_MODULES = [
	"@gramax/sdk",
	"@gramax/sdk/ui",
	"@gramax/sdk/editor",
	"@gramax/sdk/events",
	"@gramax/sdk/localization",
	"@gramax/sdk/utilities",
	"@gramax/sdk/metrics",
] as const;

const SDK_PATH = "/plugins/sdk.js";
const SDK_PATH_VITE_DEV = "/@id/@plugins/api/sdk/index";

// es-module-shims is vendored under core/public/plugins and served same-origin (next to sdk.js).
// A cross-origin CDN <script> is blocked by the app's COEP: require-corp policy
// (SharedArrayBuffer/wasm threads) and is unreachable behind an authenticating proxy / offline;
// same-origin sidesteps all of that.
const ES_MODULE_SHIM_PATH = "/plugins/es-module-shims.js";
const isWebRuntime = typeof window !== "undefined" && typeof document !== "undefined";

interface ImportShimWindow extends Window {
	importShim?: (url: string) => Promise<{ default?: unknown }>;
}

const getImportShim = () => (window as ImportShimWindow).importShim;

// Tauri serves from a custom protocol origin, so resolve against the document to keep it absolute.
function resolvePluginAssetUrl(path: string, basePath: string): string {
	const url = Url.fromBasePath(path, basePath).toString();
	if (isWebRuntime && getExecutingEnvironment() === "tauri") return new URL(url, window.location.href).href;
	return url;
}

export function getSdkUrl(basePath: string): string {
	const isWebDev = getExecutingEnvironment() === "web" && process.env.NODE_ENV === "development";
	if (isWebDev) return SDK_PATH_VITE_DEV;
	return resolvePluginAssetUrl(SDK_PATH, basePath);
}

export function getShimUrl(basePath: string): string {
	return resolvePluginAssetUrl(ES_MODULE_SHIM_PATH, basePath);
}

function createImportMap(sdkUrl: string): Record<string, string> {
	return Object.fromEntries(GRAMAX_MODULES.map((mod) => [mod, sdkUrl]));
}

function failedToLoad(what: string, url: string, cause?: unknown): Error {
	return new Error(`Plugins are not loaded: failed to load ${what} from ${url}`, { cause });
}

export class EsModuleShimsLoader {
	constructor(private readonly _basePath = "") {}

	private get isLoaded(): boolean {
		if (!isWebRuntime) return true;
		const hasImportMap = !!document.head.querySelector('script[type="importmap-shim"]');
		const hasShimScript = !!document.head.querySelector('script[src*="es-module-shims"]');
		const hasImportShim = typeof getImportShim() === "function";
		return hasImportMap && hasShimScript && hasImportShim;
	}

	async load(): Promise<void> {
		if (!isWebRuntime) {
			return;
		}

		if (this.isLoaded) {
			return;
		}

		const sdkUrl = getSdkUrl(this._basePath);
		await this._loadShim(sdkUrl);
		await this._loadSdk(sdkUrl);
	}

	private _loadShim(sdkUrl: string): Promise<void> {
		const shimUrl = getShimUrl(this._basePath);
		return new Promise((resolve, reject) => {
			const importMap = document.createElement("script");
			importMap.type = "importmap-shim";
			importMap.textContent = JSON.stringify({
				imports: createImportMap(sdkUrl),
			});
			document.head.appendChild(importMap);

			const shimScript = document.createElement("script");
			shimScript.async = true;
			shimScript.src = shimUrl;
			shimScript.onload = () => {
				resolve();
			};
			shimScript.onerror = () => {
				document.head.removeChild(importMap);
				reject(failedToLoad("es-module-shims", shimUrl));
			};
			document.head.appendChild(shimScript);
		});
	}

	// Plugins reach the SDK only through the import map, where a missing sdk.js surfaces as an opaque import error.
	private async _loadSdk(sdkUrl: string): Promise<void> {
		const importShim = getImportShim();
		assert(importShim, "es-module-shims importShim is not available");
		try {
			await importShim(sdkUrl);
		} catch (error) {
			throw failedToLoad("the plugin SDK", sdkUrl, error);
		}
	}

	async importModule<T = unknown>(scriptUrl: string): Promise<T> {
		if (!isWebRuntime) {
			const module = await import(/* webpackIgnore: true */ scriptUrl);
			assert(module?.default, `Plugin module at ${scriptUrl} does not export a default class`);
			return module.default as T;
		}

		const importShim = getImportShim();
		assert(importShim, "es-module-shims importShim is not available");
		const module = await importShim(scriptUrl);
		assert(module?.default, `Plugin module at ${scriptUrl} does not export a default class`);
		return module.default as T;
	}
}
